import { useStreamingReport } from '../../hooks/useStreamingReport';
import StreamingStatus from '../../components/StreamingStatus';
import React from 'react';

const VEDIC_FOLLOW_UP_LIMIT = 2;
import { Alert, Button, ConfigProvider, Form, Input, Radio, Select, Typography, message } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import dayjs from 'dayjs';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import '../../App.css';
import './index.css';
import BirthDateTimePicker from '../../components/BirthDateTimePicker';
import AILoadingAnimation from '../../components/AILoadingAnimation';
import XunhupayButton from '../../components/XunhupayButton';
import InviteCodeUnlock from '../../components/InviteCodeUnlock';
import WeChatGuide from '../../components/WeChatGuide';
import { siteConfig } from '../../config/site';
import { cityFilterOption, findCityByOptionValue, getCityOptionsByLocale, getCityOptionValue, type City } from '../../data/cities';
import { isWeChatBrowser } from '../../utils';
import { useExportImage } from '../../hooks/useExportImage';

const rawVedicPrice = Number(import.meta.env.VITE_VEDIC_PRICE || '9.90');
const VEDIC_PAYMENT_PRICE = Number.isFinite(rawVedicPrice) && rawVedicPrice > 0 ? rawVedicPrice : 9.9;
const VEDIC_FORM_STORAGE_KEY = 'vedic:form-draft:v1';
const VEDIC_PENDING_ORDER_STORAGE_KEY = 'vedic:pending-order:v1';
const VEDIC_USER_ID_STORAGE_KEY = 'vedic:user-id:v1';

interface VedicFormDraft {
  birthDateTime?: string;
  gender?: 'male' | 'female';
  city?: City;
  question?: string;
}

interface VedicHistoryRecord {
  recordId: string;
  createdAt: string;
  birthDate: string;
  birthTime: string;
  cityName: string;
  question: string;
  validationAnswer: string;
  previewAnswer?: string;
  answer?: string;
  reportToken?: string;
  paid?: boolean;
  hasPreview?: boolean;
  hasFullAnswer?: boolean;
  followUpHistory?: Array<{ question: string; answer: string }>;
}

function getVedicUserId() {
  let userId = localStorage.getItem(VEDIC_USER_ID_STORAGE_KEY);
  if (!userId) {
    userId = `vedic_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(VEDIC_USER_ID_STORAGE_KEY, userId);
  }
  return userId;
}

function decodeVedicReport(payload: string): { aiAnswer?: string } | null {
  try {
    return JSON.parse(decodeURIComponent(atob(payload)));
  } catch {
    return null;
  }
}

function parseValidationItems(text: string) {
  const normalized = text
    .replace(/\r/g, '\n')
    .replace(/^#+\s*前事验证.*$/gm, '')
    .replace(/请判断下面这些倾向是否贴近[:：]?/g, '')
    .replace(/不要输出选项或结尾提示。?/g, '')
    .replace(/请回复[:：]?.*$/gm, '')
    .trim();
  const matches = [...normalized.matchAll(/(?:^|\s)(\d+)[.、]\s*([\s\S]*?)(?=(?:\s\d+[.、]\s*)|$)/g)];
  const items = matches.map((match) => match[2]
    .replace(/\s+/g, ' ')
    .replace(/[。；;]\s*$/, '')
    .trim());

  if (items.length > 1) {
    return items.filter((item) => item && !/[ABC]\s*(大部分符合|一半符合|明显不符合)/.test(item));
  }

  return normalized
    .split('\n')
    .map((line) => line.replace(/^\d+[.、]\s*/, '').trim())
    .filter((item) => item && !item.startsWith('#') && !/[ABC]\s*(大部分符合|一半符合|明显不符合)/.test(item));
}

export function VedicPage() {
  const validationRef = React.useRef<HTMLElement | null>(null);
  const scrollToValidation = React.useRef(false);
  const [birthDateTime, setBirthDateTime] = React.useState<{ date: dayjs.Dayjs; isLunar: boolean } | null>(null);
  const [gender, setGender] = React.useState<'male' | 'female'>('female');
  const [city, setCity] = React.useState<City | null>(null);
  const [question, setQuestion] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const { run: runReportStream, streaming: reportStreaming, error: streamError, access: reportAccess, reportToken: streamReportToken } = useStreamingReport();
  const [answer, setAnswer] = React.useState('');
  const [currentRecordId, setCurrentRecordId] = React.useState('');
  const [reportToken, setReportToken] = React.useState('');
  const [lockedSectionTitles, setLockedSectionTitles] = React.useState<string[]>([]);
  const [previewLocked, setPreviewLocked] = React.useState(false);
  const [unlocked, setUnlocked] = React.useState(false);
  const [validationAnswer, setValidationAnswer] = React.useState('');
  const [validationFeedbacks, setValidationFeedbacks] = React.useState<Record<number, string>>({});
  const [validationLoading, setValidationLoading] = React.useState(false);
  React.useEffect(() => {
    if (validationLoading || !validationAnswer || !scrollToValidation.current) return;
    const frame = window.requestAnimationFrame(() => {
      validationRef.current?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
        block: 'start',
      });
      scrollToValidation.current = false;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [validationAnswer, validationLoading]);
  const [reportLoading, setReportLoading] = React.useState(false);
  const [followUpLoading, setFollowUpLoading] = React.useState(false);
  const [liveFollowUp, setLiveFollowUp] = React.useState('');
  const { run: runFollowUpStream, streaming: followUpStreaming, error: followUpStreamError } = useStreamingReport();
  const [followUpQuestion, setFollowUpQuestion] = React.useState('');
  const [followUpHistory, setFollowUpHistory] = React.useState<Array<{ question: string; answer: string }>>([]);
  const [paymentChecking, setPaymentChecking] = React.useState(false);
  const [isWeChat, setIsWeChat] = React.useState(false);
  const [userId, setUserId] = React.useState('');
  const [historyRecords, setHistoryRecords] = React.useState<VedicHistoryRecord[]>([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);
  const { exportImage } = useExportImage('印度占星报告');
  const remainingFollowUps = VEDIC_FOLLOW_UP_LIMIT - followUpHistory.length;
  const isPaywallActive = (previewLocked || reportAccess === 'preview') && !unlocked && !(reportStreaming && reportAccess === 'full');
  const validationItems = React.useMemo(() => parseValidationItems(validationAnswer), [validationAnswer]);
  const isValidationComplete = validationItems.length > 0 && validationItems.every((_, index) => validationFeedbacks[index]);

  const saveVedicPaymentDraft = () => {
    const draft: VedicFormDraft = {
      birthDateTime: birthDateTime?.date.toISOString(),
      gender,
      city: city || undefined,
      question,
    };
    localStorage.setItem(VEDIC_FORM_STORAGE_KEY, JSON.stringify(draft));
  };

  const submit = async () => {
    if (!birthDateTime || !city) { message.error('请填写出生日期、出生时间和出生地点'); return; }
    const activeUserId = userId || getVedicUserId();
    if (!userId) setUserId(activeUserId);
    setLoading(true);
    setValidationLoading(true);
    setAnswer('');
    setValidationAnswer('');
    setValidationFeedbacks({});
    setCurrentRecordId('');
    setReportToken('');
    setLockedSectionTitles([]);
    setPreviewLocked(false);
    setUnlocked(false);
    try {
      const response = await fetch('/api/vedic-report', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: activeUserId, gender, question, birthDate: birthDateTime!.date.format('YYYY-MM-DD'), birthTime: birthDateTime!.date.format('HH:mm'),
          cityName: city!.name, latitude: city!.latitude, longitude: city!.longitude, timezone: 8, validationOnly: true,
        }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || '前事验证生成失败');
      scrollToValidation.current = true;
      setValidationAnswer(payload.answer || '');
      setCurrentRecordId(payload.recordId || '');
      setValidationFeedbacks({});
      setFollowUpQuestion('');
      setFollowUpHistory([]);
    } catch (error: any) { message.error(error.message || '前事验证生成失败'); }
    finally { setLoading(false); setValidationLoading(false); }
  };

  const generateFullReport = async () => {
    if (!birthDateTime || !city) { message.error('出生信息缺失，请重新填写'); return; }
    if (!validationAnswer.trim()) { message.error('请先生成前事验证'); return; }
    if (!isValidationComplete) { message.error('请先完成每一道前事验证'); return; }
    const activeUserId = userId || getVedicUserId();
    if (!userId) setUserId(activeUserId);
    const validationFeedback = validationItems
      .map((item, index) => `${index + 1}. ${item}：${validationFeedbacks[index]}`)
      .join('\n');
    setLoading(true);
    setReportLoading(true);
    setAnswer('');
    setReportToken('');
    setLockedSectionTitles([]);
    setPreviewLocked(siteConfig.isPaidMode);
    setUnlocked(false);
    try {
      const response = await runReportStream('/api/vedic-report', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: activeUserId, recordId: currentRecordId, gender, question, birthDate: birthDateTime.date.format('YYYY-MM-DD'), birthTime: birthDateTime.date.format('HH:mm'),
          cityName: city.name, latitude: city.latitude, longitude: city.longitude, timezone: 8,
          validationAnswer, validationFeedback,
        }),
      }, (text) => { setAnswer(text); setLoading(false); });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || '报告生成失败');
      const showPaidPreview = payload.access ? payload.access === 'preview' : siteConfig.isPaidMode && Boolean(payload.reportToken);
      setAnswer((showPaidPreview ? payload.previewAnswer : payload.answer) || '');
      setCurrentRecordId(payload.recordId || currentRecordId);
      setReportToken(payload.reportToken || '');
      setLockedSectionTitles(Array.isArray(payload.lockedSectionTitles) ? payload.lockedSectionTitles : []);
      setPreviewLocked(showPaidPreview);
      setUnlocked(!showPaidPreview);
      setFollowUpQuestion('');
      setFollowUpHistory([]);
    } catch (error: any) { message.error(error.message || '报告生成失败'); }
    finally { setLoading(false); setReportLoading(false); }
  };

  const submitFollowUp = async () => {
    if (reportStreaming || followUpStreaming) return;
    if (isPaywallActive) { message.info('解锁完整报告后，可获得本次报告的两次补充提问机会'); return; }
    if (!answer.trim()) { message.error('请先生成人生发展分析'); return; }
    if (remainingFollowUps <= 0) { message.error('本次报告的 2 次追问机会已用完，请重新生成报告'); return; }
    const normalizedQuestion = followUpQuestion.trim();
    if (!normalizedQuestion) { message.error('请输入想继续追问的问题'); return; }
    if (!birthDateTime || !city) { message.error('出生信息缺失，请重新生成报告'); return; }
    const activeUserId = userId || getVedicUserId();
    if (!userId) setUserId(activeUserId);

    setLiveFollowUp('');
    setFollowUpLoading(true);
    try {
      const response = await runFollowUpStream('/api/vedic-report', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: activeUserId, recordId: currentRecordId, gender, birthDate: birthDateTime.date.format('YYYY-MM-DD'), birthTime: birthDateTime.date.format('HH:mm'),
          latitude: city.latitude, longitude: city.longitude, timezone: 8,
          validationAnswer, followUpQuestion: normalizedQuestion, previousAnswer: answer, followUpHistory,
        }),
      }, (text) => { setLiveFollowUp(text); });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || '追问回答生成失败');
      const followUpAnswer = payload.answer || '暂无追问回答';
      setFollowUpHistory((previous) => [...previous, { question: normalizedQuestion, answer: followUpAnswer }]);
      setLiveFollowUp('');
      setFollowUpQuestion('');
      message.success('追问回答已生成');
    } catch (error: any) { message.error(error.message || '追问失败，请稍后重试'); }
    finally { setFollowUpLoading(false); }
  };

  const unlockVedicReport = async (tradeNo: string) => {
    const activeUserId = userId || getVedicUserId();
    if (!userId) setUserId(activeUserId);
    const response = await fetch(`/api/xunhupay-report?tradeNo=${encodeURIComponent(tradeNo)}&userId=${encodeURIComponent(activeUserId)}&recordId=${encodeURIComponent(currentRecordId)}`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || '支付状态尚未确认，请稍后重试');
    const report = decodeVedicReport(data.data || '');
    if (!report?.aiAnswer) throw new Error('完整报告读取失败，请联系客服并提供订单号');
    setAnswer(report.aiAnswer);
    setUnlocked(true);
    setPreviewLocked(false);
    setReportToken('');
    loadHistory().catch(() => undefined);
    localStorage.removeItem(VEDIC_PENDING_ORDER_STORAGE_KEY);
    message.success('完整报告已解锁，已附赠两次补充追问机会');
  };

  React.useEffect(() => {
    setUserId(getVedicUserId());
    setIsWeChat(isWeChatBrowser());
  }, []);

  const loadHistory = async () => {
    const id = userId || getVedicUserId();
    setHistoryLoading(true);
    try {
      const response = await fetch(`/api/vedic-history?userId=${encodeURIComponent(id)}`);
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || '历史记录读取失败');
      setHistoryRecords(Array.isArray(payload.records) ? payload.records : []);
    } catch (error: any) {
      message.error(error.message || '历史记录读取失败');
    } finally {
      setHistoryLoading(false);
    }
  };

  const openHistoryRecord = async (recordId: string) => {
    const id = userId || getVedicUserId();
    setHistoryLoading(true);
    try {
      const response = await fetch(`/api/vedic-history?userId=${encodeURIComponent(id)}&recordId=${encodeURIComponent(recordId)}`);
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || '历史记录读取失败');
      const record = payload.record as VedicHistoryRecord;
      setCurrentRecordId(record.recordId);
      setValidationAnswer(record.validationAnswer || '');
      setAnswer(record.answer || record.previewAnswer || '');
      setReportToken(record.reportToken || '');
      setPreviewLocked(siteConfig.isPaidMode && !record.paid && Boolean(record.previewAnswer) && Boolean(record.reportToken));
      setUnlocked(!siteConfig.isPaidMode || Boolean(record.paid || record.answer));
      setFollowUpHistory(Array.isArray(record.followUpHistory) ? record.followUpHistory : []);
    } catch (error: any) {
      message.error(error.message || '历史记录读取失败');
    } finally {
      setHistoryLoading(false);
    }
  };

  React.useEffect(() => {
    try {
      const rawDraft = localStorage.getItem(VEDIC_FORM_STORAGE_KEY);
      if (rawDraft) {
        const draft = JSON.parse(rawDraft) as VedicFormDraft;
        const date = draft.birthDateTime ? dayjs(draft.birthDateTime) : null;
        if (date?.isValid()) setBirthDateTime({ date, isLunar: false });
        if (draft.gender === 'male' || draft.gender === 'female') setGender(draft.gender);
        if (draft.city && Number.isFinite(draft.city.latitude) && Number.isFinite(draft.city.longitude)) setCity(draft.city);
        if (typeof draft.question === 'string') setQuestion(draft.question);
      }
    } catch {
      localStorage.removeItem(VEDIC_FORM_STORAGE_KEY);
    }
  }, []);

  React.useEffect(() => {
    const tradeNo = new URLSearchParams(window.location.search).get('order')
      || localStorage.getItem(VEDIC_PENDING_ORDER_STORAGE_KEY);
    if (!tradeNo || !siteConfig.isPaidMode) return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 60;
    let timer: number | undefined;

    const confirmPayment = async () => {
      if (cancelled) return;
      setPaymentChecking(true);
      try {
        const response = await fetch(`/api/xunhupay-query?tradeNo=${encodeURIComponent(tradeNo)}`);
        const data = await response.json();
        if (data.status === 'SUCCESS') {
          await unlockVedicReport(tradeNo);
          window.history.replaceState({}, '', window.location.pathname);
          return;
        }
        if (data.status === 'FAILED' || data.status === 'CLOSED') {
          localStorage.removeItem(VEDIC_PENDING_ORDER_STORAGE_KEY);
          message.error('订单未完成支付，可以重新发起解锁');
          return;
        }
      } catch {
        // 回跳与异步通知偶尔存在时间差，继续确认即可。
      }

      attempts += 1;
      if (!cancelled && attempts < maxAttempts) {
        timer = window.setTimeout(confirmPayment, 2000);
        return;
      }
      if (!cancelled) setPaymentChecking(false);
    };

    confirmPayment();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, []);

  return <ConfigProvider locale={zhCN}>
    <>
      <WeChatGuide visible={isWeChat} />
      <AILoadingAnimation
        visible={loading || (followUpLoading && !liveFollowUp)}
        title={followUpLoading ? '正在分析你的追问...' : validationLoading ? '正在生成前事验证...' : '正在生成个性化发展分析...'}
        subtitle={followUpLoading ? '结合原报告、追问记录与同一份个人信息继续解读' : validationLoading ? '先用过往倾向校准出生盘，再进入完整分析' : '正在整理个人结构、人生阶段与发展重点'}
      />
      <div className="app-container vedic-page">
      <div className="app-layout vedic-layout">
        <div className="center-content">
          <div className="vedic-content">
            <section className="form-card vedic-form-card">
              <div className="vedic-brand-block">
                <span className="vedic-brand-mark">PERSONAL DEVELOPMENT · LIFE PATH</span>
                <span className="vedic-brand-divider" />
              </div>
              <div className="vedic-hero">
                <Typography.Title className="form-title">人生阶段与发展路径分析</Typography.Title>
                <Typography.Paragraph className="hero-subtitle">
                  基于出生信息生成个性化结构解读，聚焦自我认知、事业、财富、关系与人生阶段。
                </Typography.Paragraph>
              </div>

              <Form layout="vertical" className="vedic-form-grid">
                <Form.Item label="出生日期与时间" required extra="出生时间与地点会影响个人信息模型的分析维度。">
                  <BirthDateTimePicker value={birthDateTime || undefined} onChange={setBirthDateTime} locale="zh-CN" allowLunar={false} />
                </Form.Item>
                <Form.Item label="性别" required>
                  <Select value={gender} onChange={setGender} options={[{ value: 'female', label: '女' }, { value: 'male', label: '男' }]} />
                </Form.Item>
                <Form.Item label="出生地点" required className="vedic-city-field">
                  <Select showSearch value={city ? getCityOptionValue(city) : undefined} onChange={(value, option: any) => setCity(option?.city || findCityByOptionValue(value) || null)} optionFilterProp="searchText" filterOption={cityFilterOption} placeholder="选择出生城市" options={getCityOptionsByLocale('zh-CN', 'domestic')} />
                </Form.Item>
              </Form>

              <div className="vedic-question">
                <Typography.Text>想重点了解什么？<span>（可选）</span></Typography.Text>
                <Input.TextArea value={question} onChange={(event) => setQuestion(event.target.value)} placeholder="例如：我是什么样的人、事业方向、感情婚姻、财富模式、当前人生阶段" rows={3} maxLength={120} showCount />
              </div>

              <div className="vedic-actions">
                <Button type="primary" size="large" loading={validationLoading} onClick={submit} className="submit-button">先生成前事验证</Button>
                <Alert type="info" showIcon message="基于个人出生信息生成结构化发展参考。" />
              </div>
            </section>

            <section className="vedic-history-panel">
              <div>
                <Typography.Title level={3}>本机历史测试记录</Typography.Title>
                <Typography.Paragraph>记录绑定当前浏览器，每台设备最多保留 10 条；换设备、换浏览器或清理浏览器数据后可能看不到。</Typography.Paragraph>
              </div>
              <Button loading={historyLoading} onClick={loadHistory}>查看历史</Button>
              {historyRecords.length > 0 && <div className="vedic-history-list">
                {historyRecords.map((record) => (
                  <button key={record.recordId} className="vedic-history-item" onClick={() => openHistoryRecord(record.recordId)}>
                    <strong>{record.birthDate} {record.birthTime} · {record.cityName || '未知地点'}</strong>
                    <span>{record.question || '完整人生画像'}</span>
                    <em>{record.paid || record.hasFullAnswer ? '完整报告' : '预览记录'} · {dayjs(record.createdAt).format('MM-DD HH:mm')}</em>
                  </button>
                ))}
              </div>}
            </section>

            {validationAnswer && !answer && <section ref={validationRef} className="result-card vedic-validation-card" style={{ scrollMarginTop: '24px' }}>
              <Typography.Title level={2} className="result-title">前事验证</Typography.Title>
              <Typography.Paragraph className="vedic-validation-intro">请逐条判断是否贴近，系统会带着你的反馈生成完整报告。</Typography.Paragraph>
              {validationItems.length > 0 ? <div className="vedic-validation-list">
                {validationItems.map((item, index) => (
                  <article key={`${index}-${item}`} className="vedic-validation-item">
                    <div className="vedic-validation-question"><span>{index + 1}.</span>{item}</div>
                    <Radio.Group
                      className="vedic-validation-options"
                      optionType="button"
                      buttonStyle="solid"
                      value={validationFeedbacks[index]}
                      onChange={(event) => setValidationFeedbacks((previous) => ({ ...previous, [index]: event.target.value }))}
                      options={[
                        { label: '符合', value: '符合' },
                        { label: '部分符合', value: '部分符合' },
                        { label: '不符合', value: '不符合' },
                      ]}
                    />
                  </article>
                ))}
              </div> : <div className="markdown-content vedic-markdown"><ReactMarkdown remarkPlugins={[remarkGfm]}>{validationAnswer}</ReactMarkdown></div>}
              <Button type="primary" size="large" className="vedic-validation-submit" loading={reportLoading} disabled={!isValidationComplete} onClick={generateFullReport}>
                根据验证结果生成完整报告
              </Button>
            </section>}

            {answer && <section className="result-card vedic-report-card">
              <div className="vedic-report-header">
                <Typography.Title level={2} className="result-title">你的人生全景报告</Typography.Title>
                <Button type="primary" disabled={reportStreaming || Boolean(streamError) || followUpStreaming} onClick={() => exportImage('.vedic-report-export')}>保存为图片</Button>
              </div>
              <div className="vedic-report-export">
                <Typography.Title level={2} className="result-title">你的人生全景报告</Typography.Title>
                <div className="markdown-content vedic-markdown"><ReactMarkdown remarkPlugins={[remarkGfm]}>{isPaywallActive ? answer.slice(0, 500) : answer}</ReactMarkdown></div>
                <StreamingStatus active={reportStreaming} error={streamError} />
              </div>

              {isPaywallActive && <section className="vedic-paywall">
                <div className="vedic-paywall-kicker">已生成完整报告 · 当前为免费预览</div>
                <Typography.Title level={3}>后半份，才是这份分析真正落到人生选择的部分</Typography.Title>
                <Typography.Paragraph>
                  解锁后继续查看事业财富、关系家庭、身心节奏、能力迁移、未来阶段策略与行动清单，并附带 2 次针对同一份分析的补充追问。
                </Typography.Paragraph>
                <div className="vedic-paywall-grid">
                  {[
                    { title: '事业与财富密码', text: '职业容器、财富来源、留财能力、未来进账节奏。' },
                    { title: '关系与内在修复', text: '亲密模式、家庭安全感、压力源和身心节奏。' },
                    { title: '未来2-3年策略', text: '哪些阶段适合推进，哪些阶段先蓄力和防守。' },
                  ].map((item) => <div key={item.title} className="vedic-paywall-item">
                    <strong>🔒 {item.title}</strong>
                    <span>{item.text}</span>
                  </div>)}
                </div>
                {lockedSectionTitles.length > 0 && <div className="vedic-paywall-sections">已生成：{lockedSectionTitles.join('、')}。</div>}
                {reportStreaming && <p role="status">正在生成完整报告，现在可用邀请码解锁，解锁后正文会继续实时展示。</p>}
                <InviteCodeUnlock key={reportToken || streamReportToken} product="vedic" reportToken={reportToken || streamReportToken}
                  userId={userId || getVedicUserId()} recordId={currentRecordId}
                  onUnlocked={(fullAnswer) => {
                    if (fullAnswer) setAnswer(fullAnswer);
                    setUnlocked(true);
                    setPreviewLocked(false);
                    setReportToken('');
                    loadHistory().catch(() => undefined);
                  }} />
                {reportToken && <XunhupayButton
                  product="vedic"
                  reportToken={reportToken}
                  buttonText={`¥${VEDIC_PAYMENT_PRICE.toFixed(2)} 解锁完整发展路径分析`}
                  className="vedic-pay-button"
                  onOrderCreated={(tradeNo) => {
                    saveVedicPaymentDraft();
                    localStorage.setItem(VEDIC_PENDING_ORDER_STORAGE_KEY, tradeNo);
                  }}
                  onSuccess={(tradeNo) => {
                    unlockVedicReport(tradeNo).catch((error) => message.error(error.message || '解锁失败，请稍后重试'));
                  }}
                />}
                {paymentChecking && <div className="vedic-payment-checking">正在确认支付结果，完成后会自动解锁，请勿重复付款。</div>}
                <div className="vedic-paywall-note">内容仅供个人兴趣与自我探索参考，不构成职业、投资、医疗、法律或其他专业建议。</div>
              </section>}

              {!isPaywallActive && <section className="vedic-follow-up">
                <Typography.Title level={3}>继续追问</Typography.Title>
                <Typography.Paragraph>可围绕同一份分析继续细问，例如事业选择、关系模式、未来阶段或职业发展重点。</Typography.Paragraph>
                <div className={remainingFollowUps > 0 ? 'vedic-follow-up-count' : 'vedic-follow-up-count is-exhausted'}>本次报告剩余追问次数：{remainingFollowUps} / {VEDIC_FOLLOW_UP_LIMIT}</div>
                <Input.TextArea value={followUpQuestion} onChange={(event) => setFollowUpQuestion(event.target.value)} placeholder="输入你想继续追问的问题" rows={3} maxLength={160} showCount disabled={remainingFollowUps <= 0 || reportStreaming} />
                <Button type="primary" onClick={submitFollowUp} loading={followUpLoading} disabled={remainingFollowUps <= 0 || reportStreaming}>
                  {remainingFollowUps > 0 ? (followUpLoading ? '追问中...' : '提交追问') : '追问次数已用完'}
                </Button>
              </section>}

                {liveFollowUp && <section className="markdown-content" aria-busy={followUpStreaming}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{liveFollowUp}</ReactMarkdown>
                  <StreamingStatus active={followUpStreaming} error={followUpStreamError} />
                </section>}
              {!isPaywallActive && followUpHistory.length > 0 && <section className="vedic-follow-up-history">
                <Typography.Title level={3}>追问记录</Typography.Title>
                {[...followUpHistory].reverse().map((item, index) => <article key={followUpHistory.length - index + '-' + item.question} className="vedic-follow-up-item">
                  <div className="vedic-follow-up-label">追问 {followUpHistory.length - index}</div>
                  <p><strong>你问：</strong>{item.question}</p>
                  <div className="markdown-content vedic-markdown"><ReactMarkdown remarkPlugins={[remarkGfm]}>{item.answer}</ReactMarkdown></div>
                </article>)}
              </section>}
            </section>}
          </div>
        </div>
      </div>
    </div>
    </>
  </ConfigProvider>;
}
